import * as React from 'react';
import { Domain, Filespace } from 'fs-api-client';
import { getDomain, getFilespaces } from '../../services/filespace';
import { LoaderEnhancerProperties, withLoader } from './LoaderEnhancer';
import { PageRoutes } from '../root/Routes';
import { errorHandler } from '../../services/errorHandler';
import { LogManager } from '../../utils/LogManager';
import { ProfileInjectorProperties } from './ProfileInjector';
import { WebportalEvent } from '../../services/WebportalEvent';
import { RouteComponentProps } from 'react-router-dom';

interface DomainDependantProperties {
    domain: Domain;
    filespaces: Filespace[];
    fetchDomain: (showLoader: boolean) => void;
    showLoader: (callback: () => void) => void;
    hideLoader: () => void;
}

export interface DomainInjectorProperties extends
    RouteComponentProps,
    DomainDependantProperties,
    LoaderEnhancerProperties,
    ProfileInjectorProperties {
}

interface DomainInjectorState {
    domain: Domain | null;
    filespaces: Filespace[] | null;
    loading: boolean;
}

const logManager = new LogManager('DomainInjector');

type FilespacesFetchedCallback = (filespace: Filespace[]) => void;

let filespacesFetchedCallbacks: FilespacesFetchedCallback[] = [];

export function registerCallback(callback: FilespacesFetchedCallback) {
    filespacesFetchedCallbacks.push(callback);
}

export function unregisterCallback(callback: FilespacesFetchedCallback) {
    const index = filespacesFetchedCallbacks.findIndex((elem) => elem === callback);
    return filespacesFetchedCallbacks.splice(index);
}

function callOnRegisteredCallbacks(filespaces: Filespace[]) {
    filespacesFetchedCallbacks.forEach((callback) => {
        try {
            callback(filespaces);
        } catch (e) {
            logManager.error(e);
        }
    });
}

export function injectDomain<P extends DomainInjectorProperties>(component: React.ComponentType<P>)  {
    const loaderName = 'domainInjector';
    let DynamicComponent: any = withLoader(component, loaderName);

    return class DomainInjector extends React.Component<RouteComponentProps, DomainInjectorState> {
        private isCompMounted: boolean = false;
        private interval: NodeJS.Timer | null = null;
        private readonly DOMAIN_REFRESH_INTERVAL_MS: number = 10_000;

        constructor(props: RouteComponentProps) {
            super(props);

            this.fetchDomain = this.fetchDomain.bind(this);
            this.showLoader = this.showLoader.bind(this);
            this.hideLoader = this.hideLoader.bind(this);

            this.state = {
                domain: null,
                filespaces: null,
                loading: true,
            };
        }

        componentDidMount() {
            this.isCompMounted = true;
            this.fetchDomain(true);
            document.addEventListener(WebportalEvent.InvoicePaid, () => {this.fetchDomain(true); } );
        }

        componentWillUnmount() {
            this.isCompMounted = false;
            this.clearInterval();
        }

        render() {
            return (
                <DynamicComponent
                    {...this.props}
                    showLoader={this.showLoader}
                    hideLoader={this.hideLoader}
                    fetchDomain={this.fetchDomain}
                    domain={this.state.domain}
                    filespaces={this.state.filespaces}
                    loading={this.state.loading}
                />
            );
        }

        // This is for retry logic in case the domain request
        private initializeInterval(): void {
            if (this.interval) {
                return;
            }

            this.interval = setInterval(
                () => {
                    this.fetchDomain(false);
                },
                this.DOMAIN_REFRESH_INTERVAL_MS
            );
        }

        private clearInterval(): void {
            if (this.interval) {
                clearInterval(this.interval);
                this.interval = null;
            }
        }

        private showLoader(callback: () => void): void {
            this.setState(
                { loading: true },
                () => {
                    callback();
                });
        }

        private hideLoader(): void {
            this.setState({ loading: false });
        }

        private fetchDomain(showLoader: boolean = false): void {
            const callback = () => {
                const entitiesToBeLoadedCount = 2;
                let loadedEntitiesCount = 0;
                let loadedEntities: { domain: Domain | null, filespaces: Filespace[] | null } = {
                    domain: null,
                    filespaces: null
                };
                const tryFetchCompletion = () => {
                    loadedEntitiesCount++;
                    if (loadedEntitiesCount < entitiesToBeLoadedCount) { return; }

                    if (loadedEntities.domain) {
                        if (this.isCompMounted) {
                            this.setState(
                                {
                                    loading: false,
                                    domain: loadedEntities.domain,
                                    filespaces: loadedEntities.filespaces
                                },
                                () => {
                                    this.clearInterval();
                                }
                            );
                        }
                    }
                };

                const handleError = (error: any) => {
                    if (error && error.response && error.response.statusCode >= 500 && error.response.statusCode <= 505) {
                        this.handleServiceUnavailable();
                    } else {
                        errorHandler(error);
                    }
                };

                getDomain()
                    .then((response: any) => {
                        if (!response) {
                            this.handleDomainNotFound();
                        }
                        loadedEntities.domain = response;

                        tryFetchCompletion();
                    })
                    .catch(handleError);

                getFilespaces()
                    .then((response: any) => {
                        loadedEntities.filespaces = response;
                        tryFetchCompletion();
                        callOnRegisteredCallbacks(response);
                    })
                    .catch(handleError);
            };

            if (showLoader) {
                const loading = this.state.loading || showLoader;
                this.setState(
                    { loading },
                    callback
                );
            } else {
                callback();
            }
        }

        private handleDomainNotFound(): void {
            this.props.history.push(PageRoutes.EnterDomain);
            this.setState({ loading: false });
        }

        private handleServiceUnavailable(): void {
            this.setState(
            {
                loading: false
            },
            () => {
                this.initializeInterval();
            });
        }
    };
}


// WEBPACK FOOTER //
// ./src/webportal/components/hocs/DomainInjector.tsx